import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { API_URL } from "../../../config";

const ResendConfirmationModal = ({ open, onClose, orderId, onResendSuccess }) => {
  const [messageLogs, setMessageLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (open && orderId) {
      fetchMessageLogs();
    }
  }, [open, orderId]);

  const fetchMessageLogs = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/orders/${orderId}/message-logs`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 200) {
        const resp = await response.json();
        setMessageLogs(resp.data || []);
      } else if (response.status === 401) {
        setError("Unauthorized access");
      } else {
        setError("Failed to fetch message logs");
      }
    } catch (err) {
      setError("Error fetching message logs: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setResending(true);
    setError("");
    setSuccess("");
    try {
      const response = await fetch(`${API_URL}/distributor/orders/resend-confirmation-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: orderId }),
      });

      if (response.status === 200) {
        setSuccess("Confirmation email sent successfully!");
        setShowConfirm(false);
        setTimeout(() => {
          fetchMessageLogs();
          if (onResendSuccess) onResendSuccess();
        }, 1000);
      } else if (response.status === 401) {
        setError("Unauthorized access");
      } else {
        const resp = await response.json().catch(() => null);
        setError(resp?.message || "Failed to resend confirmation email");
      }
    } catch (err) {
      setError("Error resending confirmation email: " + err.message);
    } finally {
      setResending(false);
    }
  };

  const handleClose = () => {
    setError("");
    setSuccess("");
    setShowConfirm(false);
    onClose();
  };

  return (
    <>
      <Modal isOpen={open} toggle={handleClose} centered className="modern-modal resend-confirmation-modal" size="xl">
        <ModalHeader toggle={handleClose} className="modal-header-custom">
          <i className="fas fa-envelope"></i> Resend Confirmation Email
        </ModalHeader>
        <ModalBody className="modal-body-custom">
          {error && (
            <div className="alert-banner alert-error">{error}</div>
          )}
          {success && (
            <div className="alert-banner alert-success">{success}</div>
          )}

          <h4 className="section-title">
            <i className="fas fa-history"></i> Message Logs
          </h4>

          {loading ? (
            <div className="loading-state">
              <div className="loader-spinner"></div>
              <p>Loading message logs...</p>
            </div>
          ) : messageLogs.length > 0 ? (
            <div className="orders-table-container">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Message ID</th>
                    <th>Triggered By</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {messageLogs.map((log, index) => (
                    <tr key={log.id || index} className="order-row">
                      <td>{log.messageId || "N/A"}</td>
                      <td>{log.triggeredBy || "N/A"}</td>
                      <td>
                        <span
                          className={`status-badge status-${log.status === "1" ? "active" : "pending"}`}
                        >
                          {log.status === "1" ? "Sent" : "Pending"}
                        </span>
                      </td>
                      <td>{log.created ? new Date(log.created).toLocaleString() : "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <i className="fas fa-inbox"></i>
              <p>No message logs available</p>
            </div>
          )}

        </ModalBody>
        <ModalFooter className="modal-footer-custom resend-modal-footer">
          <button onClick={handleClose} className="btn-secondary resend-modal-btn">
            Close
          </button>
          <button
            onClick={() => setShowConfirm(true)}
            className="btn-primary-modern resend-modal-btn"
            disabled={resending}
          >
            {resending ? "Sending..." : "Resend Confirmation Email"}
          </button>
        </ModalFooter>
      </Modal>

      {/* Confirm Resend Dialog */}
      <Modal isOpen={showConfirm} toggle={() => setShowConfirm(false)} centered className="modern-modal">
        <ModalHeader toggle={() => setShowConfirm(false)} className="modal-header-custom">
          <i className="fas fa-envelope"></i> Confirm Resend
        </ModalHeader>
        <ModalBody className="modal-body-custom">
          <p style={{ margin: 0, fontWeight: 600 }}>
            Are you sure you want to resend the confirmation email for Order {orderId}?
          </p>
        </ModalBody>
        <ModalFooter className="modal-footer-custom resend-modal-footer">
          <button
            className="btn-secondary resend-modal-btn"
            onClick={() => setShowConfirm(false)}
            disabled={resending}
          >
            Cancel
          </button>
          <button
            className="btn-primary-modern resend-modal-btn"
            onClick={handleResendConfirmation}
            disabled={resending}
          >
            {resending ? "Sending..." : "Confirm"}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ResendConfirmationModal;
